import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg, fileUrl, INTEREST_TAGS, CATEGORIES } from "@/lib/api";
import { trackSignUp } from "@/lib/gtm";
import { trackCompleteRegistration } from "@/lib/metaPixel";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, Store, CalendarPlus, ShoppingBag, CheckCircle2, ArrowRight, ArrowLeft, Lock, Heart, Plus, X, Upload, ChevronDown, Check, Trash2, MapPin } from "lucide-react";

const EC_EVENT_TYPES = [
  "Concerts & Live Entertainment",
  "Fairs, Festivals & Markets",
  "Academic & Co-ops",
  "Arts & Creative",
  "Sports & PE",
  "Field Trips & Excursions",
  "STEM & Robotics",
  "Social & Playdates",
  "Life Skills & Workshops",
  "Teen & Tween Specials",
  "Community Service",
  "Seasonal, Holiday & Camps",
];

export const BUNDLE_ONBOARDING_STORAGE_KEY = "cck_bundle_onboarding_v1";
const BUNDLE_CREDS_KEY = "cck_bundle_creds_v1";
export const BUNDLE_COMPLETED_KEY = "cck_bundle_completed_v1";

const STEPS = [
  { key: "parent", label: "Parent", icon: Users, title: "Parent profile" },
  { key: "vendor", label: "Vendor", icon: Store, title: "Vendor profile" },
  { key: "event_creator", label: "Event Creator", icon: CalendarPlus, title: "Event Creator profile" },
  { key: "marketplace_user", label: "Marketplace", icon: ShoppingBag, title: "Marketplace profile" },
];

const MK_PRODUCT_CATEGORY_OPTIONS = [
  "Curriculum and Learning Materials",
  "Educational Toys and Games",
  "Books Media and Printables",
  "Kids Clothing and Shoes",
  "Sports PE and Outdoor Gear",
  "Arts Crafts and Music",
  "Desks and Learning Space",
  "Tech Electronics and Lab Gear",
  "Baby and Toddler Essentials",
  "Handcrafted and Local Goods",
  "Free and Community Swap",
  "Other",
];

const MK_PRODUCT_SLOT_COUNT = 8;
const mkEmptySlot = () => ({ photo: "", description: "", price: "" });
const mkPadSlots = (arr) => {
  const list = Array.isArray(arr) ? arr.slice(0, MK_PRODUCT_SLOT_COUNT).map((s) => ({
    photo: s?.photo || "",
    description: s?.description || "",
    price: s?.price || "",
  })) : [];
  while (list.length < MK_PRODUCT_SLOT_COUNT) list.push(mkEmptySlot());
  return list;
};

const EMPTY = {
  parent: { first_name: "", surname: "", children: [], interests: [], about_family: "" },
  vendor: {
    business_name: "", tagline: "", description: "", categories: [],
    location: "", phone: "", contact_email: "", website: "", social_media: "", services: [],
  },
  event_creator: {
    organizer_name: "", event_name: "", about_event: "",
    location: "", contact_number: "", email: "", social_links: "", website: "",
  },
  marketplace_user: {
    business_name: "", tagline: "", description: "", categories: [],
    location: "", phone: "", contact_email: "", website: "",
    business_logo: "",
    product_categories: [],
    product_slots: mkPadSlots([]),
    social_link_1: "", social_link_2: "", social_link_3: "",
  },
};

function Stepper({ step }) {
  return (
    <div className="flex items-center justify-between mb-8" data-testid="bundle-stepper">
      {STEPS.map((s, i) => {
        const done = i < step;
        const current = i === step;
        return (
          <div key={s.key} className="flex items-center flex-1" data-testid={`bundle-step-${s.key}`}>
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition-colors ${
                  done
                    ? "bg-sage border-sage text-white"
                    : current
                    ? "bg-white border-terracotta text-terracotta-dark ring-4 ring-terracotta/20"
                    : "bg-white border-sage/30 text-forest/40"
                }`}
                data-testid={`bundle-step-indicator-${s.key}`}
              >
                {done ? <CheckCircle2 className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
              </div>
              <span className={`text-[11px] font-bold uppercase tracking-wide ${current ? "text-terracotta-dark" : done ? "text-sage-dark" : "text-forest/40"}`}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 ${i < step ? "bg-sage" : "bg-sage/20"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ParentForm({ value, onChange, photoFile, onPhotoFile }) {
  const photoInput = useRef(null);
  const [customTag, setCustomTag] = useState("");

  const syncCount = (n) => {
    const c = Math.max(0, Math.min(12, n || 0));
    const next = Array.from({ length: c }, (_, i) => value.children[i] || { age: "", gender: "" });
    onChange({ ...value, children: next });
  };
  const setChild = (i, key, val) => {
    const arr = [...value.children];
    arr[i] = { ...arr[i], [key]: val };
    onChange({ ...value, children: arr });
  };
  const toggleTag = (t) => {
    const curr = value.interests || [];
    onChange({ ...value, interests: curr.includes(t) ? curr.filter((x) => x !== t) : [...curr, t] });
  };
  const addCustom = () => {
    const t = customTag.trim();
    const curr = value.interests || [];
    if (t && !curr.map((x) => x.toLowerCase()).includes(t.toLowerCase())) {
      onChange({ ...value, interests: [...curr, t] });
    }
    setCustomTag("");
  };

  return (
    <div className="space-y-8" data-testid="bundle-form-parent">
      <h2 className="text-2xl font-black text-forest -mt-6 mb-1">Tell us about your family</h2>
      <p className="text-forest/60 text-sm !mt-1">This helps other families see who they're meeting at events and meet-ups.</p>

      <div className="flex items-start gap-4">
        <div className="shrink-0 text-center">
          {photoFile ? (
            <img src={URL.createObjectURL(photoFile)} alt="Profile" className="w-16 h-16 rounded-full object-cover border border-sage/30" data-testid="setup-photo-preview" />
          ) : (
            <img src="/logo.png" alt="Compass Kids logo" className="w-16 h-16 rounded-full object-cover border border-sage/30 bg-white" data-testid="setup-photo-placeholder" />
          )}
          <input ref={photoInput} type="file" accept="image/*" className="hidden"
            onChange={(e) => onPhotoFile(e.target.files?.[0] || null)} data-testid="setup-photo-input" />
          <button type="button" onClick={() => photoInput.current?.click()} className="text-[11px] font-bold text-sage-dark hover:underline mt-1.5" data-testid="setup-photo-btn">
            {photoFile ? "Change" : "Add photo"}
          </button>
          {photoFile && (
            <p className="text-[10px] text-forest/50 mt-1 max-w-[9rem]">Uploads for admin approval once your bundle is saved.</p>
          )}
        </div>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-forest/80">First name *</Label>
            <Input value={value.first_name} onChange={(e) => onChange({ ...value, first_name: e.target.value })} maxLength={40}
              className="mt-1 rounded-xl bg-softstone/60 border-sage/30" data-testid="setup-first-name-input" />
          </div>
          <div>
            <Label className="text-forest/80">Surname *</Label>
            <Input value={value.surname} onChange={(e) => onChange({ ...value, surname: e.target.value })} maxLength={40}
              className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="Doe" data-testid="setup-surname-input" />
            <p className="text-[10px] text-forest/40 mt-1">Shown publicly on RSVPs and meet-ups</p>
          </div>
        </div>
      </div>

      <div>
        <Label className="text-forest/80 flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-sage-dark" /> How many children do you have?</Label>
        <Input type="number" min={0} max={12} value={value.children.length || ""}
          onChange={(e) => syncCount(parseInt(e.target.value))}
          className="w-28 rounded-xl bg-softstone/60 border-sage/30" data-testid="children-count-input" />
        <div className="space-y-2 mt-3" data-testid="children-rows">
          {value.children.map((c, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input value={c.age} onChange={(e) => setChild(i, "age", e.target.value)}
                placeholder={`Child ${i + 1} age`} className="rounded-xl bg-softstone/60 border-sage/30" data-testid={`setup-child-age-${i}`} />
              <Select value={c.gender} onValueChange={(v) => setChild(i, "gender", v)}>
                <SelectTrigger className="rounded-xl bg-softstone/60 border-sage/30" data-testid={`setup-child-gender-${i}`}>
                  <SelectValue placeholder="Gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-forest/80 flex items-center gap-2 mb-3"><Heart className="w-4 h-4 text-terracotta-dark" /> Family interests</Label>
        <div className="flex flex-wrap gap-2 mb-3" data-testid="interest-presets">
          {INTEREST_TAGS.map((t) => (
            <button key={t} type="button" onClick={() => toggleTag(t)}
              className={`text-xs font-bold px-3.5 py-2 rounded-full border transition-colors ${value.interests.includes(t) ? "bg-sage text-white border-sage" : "bg-white text-forest/70 border-sage/30 hover:border-sage"}`}
              data-testid={`interest-${t.toLowerCase()}`}>
              {t}
            </button>
          ))}
        </div>
        {value.interests.filter((t) => !INTEREST_TAGS.includes(t)).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3" data-testid="custom-tags">
            {value.interests.filter((t) => !INTEREST_TAGS.includes(t)).map((t) => (
              <span key={t} className="inline-flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-full bg-terracotta-light text-terracotta-dark">
                {t}
                <button type="button" onClick={() => toggleTag(t)} aria-label={`Remove ${t}`}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <Input value={customTag} onChange={(e) => setCustomTag(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
            placeholder="Add your own (e.g. birdwatching)" maxLength={30}
            className="rounded-full bg-softstone/60 border-sage/30" data-testid="custom-interest-input" />
          <Button type="button" variant="outline" size="sm" onClick={addCustom} disabled={!customTag.trim()}
            className="rounded-full shrink-0" data-testid="add-custom-interest-btn">
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
      </div>

      <div>
        <Label className="text-forest/80">About our family <span className="text-forest/40 font-normal">(optional)</span></Label>
        <Textarea value={value.about_family} onChange={(e) => onChange({ ...value, about_family: e.target.value })} rows={3} maxLength={500}
          placeholder="We're a nature-loving family new to homeschooling..."
          className="mt-2 rounded-xl bg-softstone/60 border-sage/30" data-testid="about-family-input" />
      </div>
    </div>
  );
}

function VendorForm({ value, onChange, logoFile, onLogoFile }) {
  const logoInput = useRef(null);
  const toggleCategory = (c) => {
    const curr = value.categories || [];
    onChange({ ...value, categories: curr.includes(c) ? curr.filter((x) => x !== c) : [...curr, c] });
  };
  const setService = (i, key, val) => {
    const arr = [...(value.services || [])];
    arr[i] = { ...arr[i], [key]: val };
    onChange({ ...value, services: arr });
  };
  return (
    <div className="space-y-5" data-testid="bundle-form-vendor">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-forest/80">Business name *</Label>
          <Input value={value.business_name} onChange={(e) => onChange({ ...value, business_name: e.target.value })}
            className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="Willow Creek Learning Co." data-testid="business-name-input" />
        </div>
        <div>
          <Label className="text-forest/80">Tagline</Label>
          <Input value={value.tagline} onChange={(e) => onChange({ ...value, tagline: e.target.value })}
            className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="One-line pitch" data-testid="tagline-input" />
        </div>
      </div>
      <div>
        <Label className="text-forest/80">Description</Label>
        <Textarea value={value.description} onChange={(e) => onChange({ ...value, description: e.target.value })}
          rows={4} className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="Tell families what you offer, ages served, approach..." data-testid="description-input" />
      </div>
      <div>
        <Label className="text-forest/80 block mb-2">Categories</Label>
        <div className="flex flex-wrap gap-2" data-testid="category-picker">
          {CATEGORIES.map((c) => (
            <button key={c} type="button" onClick={() => toggleCategory(c)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${value.categories.includes(c) ? "bg-sage text-white border-sage" : "bg-white text-forest/70 border-sage/30 hover:border-sage"}`}
              data-testid={`cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {c}
            </button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-forest/80">Location / service area</Label>
          <Input value={value.location} onChange={(e) => onChange({ ...value, location: e.target.value })}
            className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="Maple Grove" data-testid="location-input" />
        </div>
        <div>
          <Label className="text-forest/80">Phone</Label>
          <Input value={value.phone} onChange={(e) => onChange({ ...value, phone: e.target.value })}
            className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="(555) 123-4567" data-testid="phone-input" />
        </div>
        <div>
          <Label className="text-forest/80">Public contact email</Label>
          <Input value={value.contact_email} onChange={(e) => onChange({ ...value, contact_email: e.target.value })}
            className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="hello@yourbusiness.com" data-testid="contact-email-input" />
        </div>
        <div>
          <Label className="text-forest/80">Website</Label>
          <Input value={value.website} onChange={(e) => onChange({ ...value, website: e.target.value })}
            className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="https://..." data-testid="website-input" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-forest/80">Social media</Label>
          <Input value={value.social_media} onChange={(e) => onChange({ ...value, social_media: e.target.value })}
            className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="Instagram, Facebook or TikTok handle / link" data-testid="social-media-input" />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-forest/80">Services</Label>
          <Button type="button" size="sm" variant="outline" className="rounded-full"
            onClick={() => onChange({ ...value, services: [...(value.services || []), { name: "", description: "", price: "" }] })}
            data-testid="add-service-btn">
            <Plus className="w-4 h-4 mr-1" /> Add service
          </Button>
        </div>
        <div className="space-y-3" data-testid="services-list">
          {(value.services || []).map((s, i) => (
            <div key={i} className="bg-softstone/60 rounded-2xl p-4 grid sm:grid-cols-[1fr_1fr_140px_40px] gap-3 items-start">
              <Input value={s.name} onChange={(e) => setService(i, "name", e.target.value)}
                placeholder="Service name" className="rounded-xl bg-white border-sage/30" data-testid={`service-name-${i}`} />
              <Input value={s.description} onChange={(e) => setService(i, "description", e.target.value)}
                placeholder="Short description" className="rounded-xl bg-white border-sage/30" data-testid={`service-desc-${i}`} />
              <Input value={s.price} onChange={(e) => setService(i, "price", e.target.value)}
                placeholder="R price" className="rounded-xl bg-white border-sage/30" data-testid={`service-price-${i}`} />
              <button type="button" onClick={() => onChange({ ...value, services: value.services.filter((_, j) => j !== i) })}
                className="p-2 text-terracotta-dark hover:bg-terracotta-light rounded-xl" data-testid={`service-remove-${i}`} aria-label="Remove service">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          {(!value.services || value.services.length === 0) && <p className="text-xs text-forest/50">No services yet — add your first one above.</p>}
        </div>
      </div>

      <div className="pt-2 border-t border-sage/15">
        <h3 className="font-heading font-bold text-forest mb-1">Logo</h3>
        <p className="text-xs text-forest/50 mb-4">Shown at the top of your directory card. JPG/PNG/WebP, max 10MB. Uploads once your bundle sign-up finishes.</p>
        <div className="flex items-center gap-4">
          {logoFile ? (
            <div className="relative">
              <img src={URL.createObjectURL(logoFile)} alt="Logo preview" className="w-24 h-24 rounded-2xl object-cover border border-sage/20" data-testid="logo-preview" />
              <button type="button" onClick={() => onLogoFile(null)} className="absolute -top-2 -right-2 bg-terracotta text-white rounded-full p-1 shadow" data-testid="logo-remove-btn" aria-label="Remove logo">
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="w-24 h-24 rounded-2xl bg-sage-light flex items-center justify-center">
              <Store className="w-8 h-8 text-sage-dark" />
            </div>
          )}
          <input ref={logoInput} type="file" accept="image/*" className="hidden"
            onChange={(e) => onLogoFile(e.target.files?.[0] || null)} data-testid="logo-file-input" />
          <Button type="button" variant="outline" className="rounded-full" onClick={() => logoInput.current?.click()} data-testid="logo-upload-btn">
            <Upload className="w-4 h-4 mr-2" /> {logoFile ? "Change logo" : "Upload logo"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EventCreatorForm({ value, onChange, logoFile, onLogoFile }) {
  const logoInput = useRef(null);
  const selected = (value.event_name || "").split("|").map((s) => s.trim()).filter(Boolean);
  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter((x) => x !== opt) : [...selected, opt];
    onChange({ ...value, event_name: next.join(" | ") });
  };
  return (
    <div className="space-y-5" data-testid="bundle-form-event-creator">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-full bg-sage flex items-center justify-center"><CalendarPlus className="w-5 h-5 text-white" /></span>
        <div>
          <h2 className="text-xl font-black text-forest">Create Your Business Profile</h2>
          <p className="text-sm text-forest/60">Tell families about your Business</p>
        </div>
      </div>

      <div>
        <Label>Company Logo</Label>
        <div className="mt-2 flex items-center gap-4">
          {logoFile ? (
            <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-sage/30 shrink-0">
              <img src={URL.createObjectURL(logoFile)} alt="Company Logo" className="w-full h-full object-cover" data-testid="company-logo-preview" />
              <button type="button" onClick={() => onLogoFile(null)}
                className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 rounded-full text-terracotta-dark"
                aria-label="Remove image" data-testid="company-logo-remove-btn">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="w-24 h-24 rounded-2xl bg-softstone/60 border border-dashed border-sage/40 flex items-center justify-center text-forest/40 shrink-0">
              <Upload className="w-6 h-6" />
            </div>
          )}
          <div className="flex-1">
            <input ref={logoInput} type="file" accept="image/*" className="hidden"
              onChange={(e) => onLogoFile(e.target.files?.[0] || null)} data-testid="company-logo-input" />
            <Button type="button" variant="outline" className="rounded-full border-sage/40 text-forest"
              onClick={() => logoInput.current?.click()} data-testid="company-logo-btn">
              <Upload className="w-4 h-4 mr-2" /> {logoFile ? "Replace image" : "Upload image"}
            </Button>
            <p className="text-xs text-forest/50 mt-1">Square JPG or PNG, up to 10MB. Uploads once your bundle sign-up finishes.</p>
          </div>
        </div>
      </div>

      <div>
        <Label>Organizer / Business Name*</Label>
        <Input value={value.organizer_name} onChange={(e) => onChange({ ...value, organizer_name: e.target.value })}
          data-testid="ec-organizer-name" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="e.g. Little Explorers Co." />
      </div>

      <div>
        <Label>Type of Events you Host</Label>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button"
              className="mt-1 w-full min-h-[42px] flex items-center justify-between gap-2 rounded-xl bg-softstone/60 border border-sage/30 px-3 py-2 text-left hover:bg-softstone/80 transition-colors"
              data-testid="ec-event-types-trigger">
              <div className="flex flex-wrap gap-1.5 flex-1">
                {selected.length === 0 ? (
                  <span className="text-sm text-forest/50">Select one or more event types</span>
                ) : (
                  <span className="text-sm font-semibold text-forest" data-testid="ec-event-types-count">{selected.length} selected</span>
                )}
              </div>
              <ChevronDown className="w-4 h-4 text-forest/50 shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[min(26rem,calc(100vw-2rem))] rounded-2xl bg-cream border-sage/20 p-2 shadow-lg" data-testid="ec-event-types-panel">
            <div className="flex items-center justify-between px-3 py-1">
              <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50">Pick every type that applies</p>
              <button type="button" onClick={() => onChange({ ...value, event_name: "" })}
                disabled={selected.length === 0}
                className="text-[11px] font-bold text-terracotta-dark hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
                data-testid="ec-event-types-clear">
                Clear list
              </button>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {EC_EVENT_TYPES.map((opt) => {
                const on = selected.includes(opt);
                return (
                  <button key={opt} type="button" onClick={() => toggle(opt)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-sm text-left transition-colors ${on ? "bg-sage-light text-sage-dark font-semibold" : "text-forest/80 hover:bg-softstone/70"}`}
                    data-testid={`ec-event-type-option-${opt}`}>
                    <span>{opt}</span>
                    {on && <Check className="w-4 h-4 text-sage-dark shrink-0" />}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div>
        <Label>About your Business.</Label>
        <Textarea value={value.about_event} onChange={(e) => onChange({ ...value, about_event: e.target.value })}
          data-testid="ec-about-event" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" rows={4}
          placeholder="Tell us more about your business and the type of events you create." />
      </div>

      <div>
        <Label className="flex items-center gap-1"><MapPin className="w-4 h-4 text-sage-dark" /> Business Location</Label>
        <Input value={value.location} onChange={(e) => onChange({ ...value, location: e.target.value })}
          data-testid="ec-location" className="mt-1 rounded-xl bg-softstone/60 border-sage/30"
          placeholder="e.g. Kirstenbosch Gardens, Cape Town" />
      </div>

      <div>
        <Label>Contact Number</Label>
        <Input value={value.contact_number} onChange={(e) => onChange({ ...value, contact_number: e.target.value })}
          data-testid="ec-contact-number" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="e.g. +27 82 000 0000" />
      </div>

      <div>
        <Label>Email Address</Label>
        <Input type="email" value={value.email} onChange={(e) => onChange({ ...value, email: e.target.value })}
          data-testid="ec-email" className="mt-1 rounded-xl bg-softstone/60 border-sage/30" placeholder="you@yourdomain.co.za" />
      </div>

      <div>
        <Label>Social Links</Label>
        <Input value={value.social_links} onChange={(e) => onChange({ ...value, social_links: e.target.value })}
          data-testid="ec-social-links" className="mt-1 rounded-xl bg-softstone/60 border-sage/30"
          placeholder="Instagram, Facebook or website URL" />
      </div>

      <div>
        <Label>Website</Label>
        <Input type="url" value={value.website} onChange={(e) => onChange({ ...value, website: e.target.value })}
          data-testid="ec-website" className="mt-1 rounded-xl bg-softstone/60 border-sage/30"
          placeholder="https://yourbusiness.co.za" />
      </div>
    </div>
  );
}

function MarketplaceForm({ value, onChange, logoFile, onLogoFile, slotFiles, onSlotFile }) {
  const logoInput = useRef(null);
  const slotRefs = useRef(Array.from({ length: MK_PRODUCT_SLOT_COUNT }, () => ({ current: null })));
  const selectedCats = value.product_categories || [];
  const toggleCategory = (opt) => {
    const next = selectedCats.includes(opt) ? selectedCats.filter((x) => x !== opt) : [...selectedCats, opt];
    onChange({ ...value, product_categories: next });
  };
  const setSlot = (idx, patch) => {
    const next = (value.product_slots || mkPadSlots([])).slice();
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...value, product_slots: next });
  };
  const slots = value.product_slots || mkPadSlots([]);

  return (
    <div className="space-y-4" data-testid="bundle-form-marketplace">
      <div className="flex items-center gap-3">
        <span className="w-11 h-11 rounded-full bg-sage flex items-center justify-center"><ShoppingBag className="w-5 h-5 text-white" /></span>
        <div>
          <h2 className="text-xl font-black text-forest">Marketplace seller profile</h2>
          <p className="text-sm text-forest/60">Set up your shop so families can find you</p>
        </div>
      </div>

      <div>
        <Label>Business Logo</Label>
        <div className="mt-2 flex items-center gap-4">
          {logoFile ? (
            <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-sage/30 shrink-0">
              <img src={URL.createObjectURL(logoFile)} alt="Business logo" className="w-full h-full object-cover" data-testid="mk-business-logo-preview" />
              <button type="button" onClick={() => onLogoFile(null)} className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 rounded-full text-terracotta-dark" aria-label="Remove logo" data-testid="mk-business-logo-remove">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <div className="w-24 h-24 rounded-2xl bg-softstone/60 border border-dashed border-sage/40 flex items-center justify-center text-forest/40 shrink-0">
              <Upload className="w-6 h-6" />
            </div>
          )}
          <div>
            <input ref={logoInput} type="file" accept="image/*" className="hidden"
              onChange={(e) => onLogoFile(e.target.files?.[0] || null)} data-testid="mk-business-logo-input" />
            <Button type="button" variant="outline" className="rounded-full border-sage/40 text-forest"
              onClick={() => logoInput.current?.click()} data-testid="mk-business-logo-btn">
              <Upload className="w-4 h-4 mr-2" /> {logoFile ? "Replace logo" : "Upload logo"}
            </Button>
            <p className="text-xs text-forest/50 mt-1">Square JPG or PNG, up to 10MB. Uploads once your bundle sign-up finishes.</p>
          </div>
        </div>
      </div>

      <div>
        <Label>Shop / seller name*</Label>
        <Input value={value.business_name} onChange={(e) => onChange({ ...value, business_name: e.target.value })} data-testid="mk-business-name" className="mt-1" />
      </div>
      <div>
        <Label>Tagline</Label>
        <Input value={value.tagline} onChange={(e) => onChange({ ...value, tagline: e.target.value })} data-testid="mk-tagline" className="mt-1" placeholder="e.g. Handmade wooden toys" />
      </div>
      <div>
        <Label>About your shop</Label>
        <Textarea value={value.description} onChange={(e) => onChange({ ...value, description: e.target.value })} data-testid="mk-description" className="mt-1" rows={4} placeholder="What you sell, materials, delivery / collection info" />
      </div>

      <div>
        <Label>Product Categories</Label>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button"
              className="mt-1 w-full min-h-[42px] flex items-center justify-between gap-2 rounded-xl bg-softstone/60 border border-sage/30 px-3 py-2 text-left hover:bg-softstone/80 transition-colors"
              data-testid="mk-product-categories-trigger">
              <div className="flex flex-wrap gap-1.5 flex-1">
                {selectedCats.length === 0 ? (
                  <span className="text-sm text-forest/50">Select one or more product categories</span>
                ) : (
                  <span className="text-sm font-semibold text-forest" data-testid="mk-product-categories-count">{selectedCats.length} selected</span>
                )}
              </div>
              <ChevronDown className="w-4 h-4 text-forest/50 shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[min(26rem,calc(100vw-2rem))] rounded-2xl bg-cream border-sage/20 p-2 shadow-lg" data-testid="mk-product-categories-panel">
            <div className="flex items-center justify-between px-3 py-1">
              <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50">Pick every category that applies</p>
              <button type="button" onClick={() => onChange({ ...value, product_categories: [] })}
                disabled={selectedCats.length === 0}
                className="text-[11px] font-bold text-terracotta-dark hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
                data-testid="mk-product-categories-clear">
                Clear Selection
              </button>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {MK_PRODUCT_CATEGORY_OPTIONS.map((opt) => {
                const on = selectedCats.includes(opt);
                return (
                  <button key={opt} type="button" onClick={() => toggleCategory(opt)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-sm text-left transition-colors ${on ? "bg-sage-light text-sage-dark font-semibold" : "text-forest/80 hover:bg-softstone/70"}`}
                    data-testid={`mk-product-category-option-${opt}`}>
                    <span>{opt}</span>
                    {on && <Check className="w-4 h-4 text-sage-dark shrink-0" />}
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
        {selectedCats.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="mk-product-categories-chips">
            {selectedCats.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 text-xs font-semibold text-sage-dark bg-sage-light/70 border border-sage/30 rounded-full pl-2.5 pr-1 py-0.5">
                {c}
                <button type="button" onClick={() => toggleCategory(c)} className="rounded-full hover:bg-white/60 p-0.5" aria-label={`Remove ${c}`}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Location / area</Label>
          <Input value={value.location} onChange={(e) => onChange({ ...value, location: e.target.value })} data-testid="mk-location" className="mt-1" />
        </div>
        <div>
          <Label>Contact phone</Label>
          <Input value={value.phone} onChange={(e) => onChange({ ...value, phone: e.target.value })} data-testid="mk-phone" className="mt-1" />
        </div>
        <div>
          <Label>Contact email</Label>
          <Input value={value.contact_email} onChange={(e) => onChange({ ...value, contact_email: e.target.value })} data-testid="mk-email" className="mt-1" />
        </div>
        <div>
          <Label>Website / social link</Label>
          <Input value={value.website} onChange={(e) => onChange({ ...value, website: e.target.value })} data-testid="mk-website" className="mt-1" />
        </div>
      </div>

      <div className="grid gap-3">
        <div>
          <Label>Social Media Link 1</Label>
          <Input value={value.social_link_1} onChange={(e) => onChange({ ...value, social_link_1: e.target.value })} placeholder="https://" className="mt-1" data-testid="mk-social-link-1" />
        </div>
        <div>
          <Label>Social Media Link 2</Label>
          <Input value={value.social_link_2} onChange={(e) => onChange({ ...value, social_link_2: e.target.value })} placeholder="https://" className="mt-1" data-testid="mk-social-link-2" />
        </div>
        <div>
          <Label>Social Media Link 3</Label>
          <Input value={value.social_link_3} onChange={(e) => onChange({ ...value, social_link_3: e.target.value })} placeholder="https://" className="mt-1" data-testid="mk-social-link-3" />
        </div>
      </div>

      <div className="pt-4 border-t border-sage/20">
        <h3 className="text-lg font-heading font-bold text-forest mb-1">Products</h3>
        <p className="text-sm text-forest/60 mb-4">Add up to 8 products. Each product has its own photo, description and price.</p>
        <div className="space-y-5" data-testid="mk-product-slots">
          {slots.map((slot, idx) => {
            const file = slotFiles[idx];
            return (
              <div key={idx} className="rounded-2xl border border-sage/20 bg-softstone/30 p-4" data-testid={`mk-product-slot-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-forest/60">Product {idx + 1}</p>
                </div>
                <div className="flex items-start gap-4">
                  {file ? (
                    <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-sage/30 shrink-0">
                      <img src={URL.createObjectURL(file)} alt={`Product ${idx + 1}`} className="w-full h-full object-cover" data-testid={`mk-slot-photo-${idx}`} />
                      <button type="button" onClick={() => onSlotFile(idx, null)} className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 rounded-full text-terracotta-dark" aria-label="Remove photo" data-testid={`mk-slot-photo-remove-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-2xl bg-white/60 border border-dashed border-sage/40 flex items-center justify-center text-forest/40 shrink-0">
                      <Upload className="w-6 h-6" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <input
                      ref={(el) => { slotRefs.current[idx] = { current: el }; }}
                      type="file" accept="image/*" className="hidden"
                      onChange={(e) => onSlotFile(idx, e.target.files?.[0] || null)}
                      data-testid={`mk-slot-photo-input-${idx}`}
                    />
                    <Button type="button" variant="outline" size="sm" className="rounded-full border-sage/40 text-forest"
                      onClick={() => slotRefs.current[idx]?.current?.click()}
                      data-testid={`mk-slot-photo-btn-${idx}`}>
                      <Upload className="w-4 h-4 mr-2" /> {file ? "Replace image" : "Upload image"}
                    </Button>
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-forest/80 text-sm">Description</Label>
                  <Textarea value={slot.description} onChange={(e) => setSlot(idx, { description: e.target.value })}
                    rows={2} placeholder="What the product is, condition, age range..."
                    className="mt-1 rounded-xl bg-white border-sage/30" data-testid={`mk-slot-description-${idx}`} />
                </div>
                <div className="mt-3">
                  <Label className="text-forest/80 text-sm">Price (Rands)</Label>
                  <div className="mt-1 relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-forest/60 font-bold">R</span>
                    <Input value={slot.price} onChange={(e) => setSlot(idx, { price: e.target.value })}
                      placeholder="e.g. 150" className="pl-7 rounded-xl bg-white border-sage/30" data-testid={`mk-slot-price-${idx}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function BundleOnboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [pendingNotice, setPendingNotice] = useState(false);
  const [credsEmail, setCredsEmail] = useState("");
  const [credsName, setCredsName] = useState("");
  const [credsFirstName, setCredsFirstName] = useState("");
  const [credsSurname, setCredsSurname] = useState("");
  const [credsPassword, setCredsPassword] = useState("");
  const [credsReady, setCredsReady] = useState(false);
  // Payments-off flow: emailed confirmation token replaces the password.
  const [verifyToken, setVerifyToken] = useState("");
  // The parent profile photo is uploaded after /auth/bundle-complete has
  // provisioned the user, since /profile/photo requires an authenticated
  // session. We never persist the File blob to localStorage.
  const [parentPhoto, setParentPhoto] = useState(null);
  // Vendor logo (not persisted in localStorage — File blobs aren't JSON-safe).
  // Uploaded via /vendor/upload after /auth/bundle-complete signs the user in.
  const [vendorLogo, setVendorLogo] = useState(null);
  // Event Creator logo (persists after bundle-complete via /event-creator/upload).
  const [eventCreatorLogo, setEventCreatorLogo] = useState(null);
  // Marketplace Seller business logo + per-slot product photos. All uploaded
  // after /auth/bundle-complete under the marketplace_user identity.
  const [marketplaceLogo, setMarketplaceLogo] = useState(null);
  const [marketplaceSlotFiles, setMarketplaceSlotFiles] = useState(() => Array(MK_PRODUCT_SLOT_COUNT).fill(null));
  const setMarketplaceSlotFile = (idx, file) => {
    setMarketplaceSlotFiles((arr) => {
      const next = arr.slice();
      next[idx] = file;
      return next;
    });
  };

  // Hydrate from localStorage on mount so a bundle user can close the browser
  // mid-flow and pick up exactly where they left off next time they land here.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(BUNDLE_ONBOARDING_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed.step === "number" && parsed.step >= 0 && parsed.step < STEPS.length) {
          setStep(parsed.step);
        }
        if (parsed.data && typeof parsed.data === "object") {
          setData((d) => ({
            parent: { ...d.parent, ...(parsed.data.parent || {}) },
            vendor: { ...d.vendor, ...(parsed.data.vendor || {}) },
            event_creator: { ...d.event_creator, ...(parsed.data.event_creator || {}) },
            marketplace_user: { ...d.marketplace_user, ...(parsed.data.marketplace_user || {}) },
          }));
        }
        if (parsed.step > 0) {
          toast.message("Picked up where you left off — step " + (parsed.step + 1) + " of " + STEPS.length + ".");
        }
      }
    } catch { /* ignore corrupt localStorage */ }
    setHydrated(true);
    try {
      const raw = sessionStorage.getItem(BUNDLE_CREDS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setCredsEmail(parsed.email || "");
        setCredsName(parsed.name || "");
        setCredsFirstName(parsed.first_name || "");
        setCredsSurname(parsed.surname || "");
        setCredsPassword(parsed.password || "");
        setVerifyToken(parsed.verify_token || "");
        if (parsed.email && (parsed.password || parsed.verify_token)) setCredsReady(true);
        // Pre-populate the parent-form first_name / surname from sign-up so
        // the user doesn't have to re-type them. Only fill blanks — never
        // clobber whatever they've already typed into the ParentForm.
        if (parsed.first_name || parsed.surname) {
          setData((d) => ({
            ...d,
            parent: {
              ...d.parent,
              first_name: d.parent.first_name || parsed.first_name || "",
              surname: d.parent.surname || parsed.surname || "",
            },
          }));
        }
      }
    } catch { /* ignore */ }
    const token = new URLSearchParams(window.location.search).get("verify");
    if (token) {
      api.get("/auth/bundle-verify", { params: { token } })
        .then(({ data: v }) => {
          const creds = { email: v.email, name: v.name, first_name: v.first_name, surname: v.surname, verify_token: token };
          try { sessionStorage.setItem(BUNDLE_CREDS_KEY, JSON.stringify(creds)); } catch { /* storage disabled */ }
          setCredsEmail(v.email); setCredsName(v.name); setCredsFirstName(v.first_name); setCredsSurname(v.surname);
          setVerifyToken(token);
          setCredsReady(true);
          setData((d) => ({ ...d, parent: { ...d.parent, first_name: d.parent.first_name || v.first_name || "", surname: d.parent.surname || v.surname || "" } }));
          toast.success("Email confirmed — let's set up your 4 profiles!");
          window.history.replaceState(null, "", "/bundle-onboarding");
        })
        .catch((e) => toast.error(errMsg(e, "This confirmation link is invalid or expired.")));
    }
  }, []);

  // Persist every state change so a browser close never loses progress.
  useEffect(() => {
    if (!hydrated || pendingNotice) return;
    try {
      localStorage.setItem(
        BUNDLE_ONBOARDING_STORAGE_KEY,
        JSON.stringify({ step, data, updated_at: new Date().toISOString() }),
      );
    } catch { /* storage full / disabled — fail quiet */ }
  }, [step, data, hydrated, pendingNotice]);

  const current = STEPS[step];
  const total = STEPS.length;

  const next = async () => {
    const d = data[current.key];
    // Minimal validation: require at least one key field per role so the user
    // doesn't skip through an empty form.
    const required = {
      parent: d.first_name && d.surname,
      vendor: d.business_name,
      event_creator: d.organizer_name,
      marketplace_user: d.business_name,
    }[current.key];
    if (!(typeof required === "string" ? required.trim() : required)) {
      toast.error("Please fill in the required field before continuing.");
      return;
    }
    setSaving(true);
    try {
      if (step < total - 1) {
        setStep(step + 1);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        // Final step — provision all four roles against the backend using the
        // credentials captured at sign-up (sessionStorage) or whatever the user
        // just filled into the inline password form.
        if (!verifyToken && (!credsEmail || !credsPassword || credsPassword.length < 6)) {
          toast.error("Please enter your email and a password (6+ characters) to finish.");
          return;
        }
        // The user doc's `name` field is used verbatim as the display name
        // on every public card this user creates (events `creator_name`, RSVP
        // `parent_name`, meet-up chat `name`, etc.). Per the Bundle brief,
        // the SURNAME is the public display name — so send surname (not the
        // first+surname concat captured at sign-up) as `name`. Fallback to
        // the originally captured credsName if the surname somehow isn't
        // filled in by step 1.
        const displayName = (data.parent.surname || "").trim()
          || credsSurname
          || credsName
          || credsEmail.split("@")[0];
        const payload = {
          email: credsEmail,
          name: displayName,
          password: credsPassword,
          ...(verifyToken ? { verify_token: verifyToken } : {}),
          profiles: data,
          ...(() => {
            try { return JSON.parse(sessionStorage.getItem("cck_bundle_checkout_v1") || "{}"); }
            catch { return {}; }
          })(),
        };
        try {
          await api.post("/auth/bundle-complete", payload);
          trackSignUp("bundle", "email");
          trackCompleteRegistration("bundle");
        } catch (e) {
          toast.error(errMsg(e, "Could not finish bundle sign-up"));
          return;
        }
        // Save the richer parent profile (children, interests, about) now
        // that the user is authenticated. Mirrors ProfileSetup's PUT /profile.
        try {
          await api.put("/profile", {
            first_name: data.parent.first_name,
            surname: data.parent.surname,
            children: (data.parent.children || []).filter((c) => String(c.age).trim()),
            interests: data.parent.interests || [],
            about_family: data.parent.about_family || "",
          });
        } catch { /* non-fatal — the bundle account is live either way */ }
        if (parentPhoto) {
          try {
            const fd = new FormData();
            fd.append("file", parentPhoto);
            await api.post("/profile/photo", fd);
          } catch { /* best-effort */ }
        }
        // Upload the vendor logo under the vendor identity by hopping into the
        // vendor role (same /auth/switch-role flow bundle users use day-to-day),
        // then hop back to parent so the user lands on the Parent dashboard.
        if (vendorLogo) {
          try {
            await api.post("/auth/switch-role", { role: "vendor" });
            const fd = new FormData();
            fd.append("file", vendorLogo);
            await api.post("/vendor/upload?kind=logo", fd);
          } catch { /* best-effort */ }
          try { await api.post("/auth/switch-role", { role: "parent" }); } catch {}
        }
        if (eventCreatorLogo) {
          try {
            await api.post("/auth/switch-role", { role: "event_creator" });
            const fd = new FormData();
            fd.append("file", eventCreatorLogo);
            const { data: upd } = await api.post("/event-creator/upload?kind=logo", fd);
            try {
              await api.put("/role-profile", { role: "event_creator", profile: { company_logo: upd?.path || "" } });
            } catch {}
          } catch { /* best-effort */ }
          try { await api.post("/auth/switch-role", { role: "parent" }); } catch {}
        }
        // Marketplace: upload business logo + product-slot photos under the
        // marketplace_user identity, then persist the full richer profile
        // (categories, socials, slot captions / prices) via PUT /role-profile.
        const hasMkMedia = marketplaceLogo || marketplaceSlotFiles.some(Boolean);
        const mkData = data.marketplace_user || {};
        const needMkWrite = hasMkMedia || (mkData.product_categories && mkData.product_categories.length) || mkData.social_link_1 || mkData.social_link_2 || mkData.social_link_3 || (mkData.product_slots && mkData.product_slots.some((s) => s && (s.description || s.price)));
        if (needMkWrite) {
          try {
            await api.post("/auth/switch-role", { role: "marketplace_user" });
            let logoPath = "";
            if (marketplaceLogo) {
              try {
                const fd = new FormData();
                fd.append("file", marketplaceLogo);
                const { data: upd } = await api.post("/marketplace-user/upload?kind=logo", fd);
                logoPath = upd?.path || "";
              } catch {}
            }
            const slots = (mkData.product_slots || mkPadSlots([])).map((s) => ({ ...s }));
            for (let i = 0; i < marketplaceSlotFiles.length; i++) {
              const f = marketplaceSlotFiles[i];
              if (!f) continue;
              try {
                const fd = new FormData();
                fd.append("file", f);
                const { data: upd } = await api.post("/marketplace-user/upload?kind=product_photo", fd);
                if (upd?.path) slots[i] = { ...slots[i], photo: upd.path };
              } catch {}
            }
            try {
              await api.put("/role-profile", {
                business_name: mkData.business_name || "",
                tagline: mkData.tagline || "",
                description: mkData.description || "",
                location: mkData.location || "",
                phone: mkData.phone || "",
                contact_email: mkData.contact_email || "",
                website: mkData.website || "",
                business_logo: logoPath,
                product_categories: mkData.product_categories || [],
                product_slots: slots,
                social_link_1: mkData.social_link_1 || "",
                social_link_2: mkData.social_link_2 || "",
                social_link_3: mkData.social_link_3 || "",
              });
            } catch {}
          } catch { /* best-effort */ }
          try { await api.post("/auth/switch-role", { role: "parent" }); } catch {}
        }
        try { localStorage.removeItem(BUNDLE_ONBOARDING_STORAGE_KEY); } catch {}
        try { localStorage.setItem(BUNDLE_COMPLETED_KEY, JSON.stringify({ email: credsEmail })); } catch {}
        try { sessionStorage.removeItem(BUNDLE_CREDS_KEY); } catch {}
        try { sessionStorage.removeItem("cck_bundle_checkout_v1"); } catch {}
        try { await refresh(); } catch {}
        setPendingNotice(true);
      }
    } finally {
      setSaving(false);
    }
  };

  const back = () => {
    if (step === 0) return;
    setStep(step - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const update = (val) => setData((d) => ({ ...d, [current.key]: val }));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 grain" data-testid="bundle-onboarding-page">
      <div className="text-center mb-6 fade-in">
        <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase">Bundle onboarding</p>
        <h1 className="text-2xl sm:text-3xl font-black text-forest mt-1" data-testid="bundle-onboarding-title">
          Set up your 4 roles
        </h1>
        <p className="text-forest/60 mt-1 text-sm" data-testid="bundle-onboarding-step-label">
          Step {step + 1} of {total} — {current.label}
        </p>
      </div>

      <Stepper step={step} />

      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm">
        <h2 className="text-lg font-heading font-bold text-forest mb-4" data-testid="bundle-form-title">
          {current.title}
        </h2>
        {current.key === "parent" && <ParentForm value={data.parent} onChange={update} photoFile={parentPhoto} onPhotoFile={setParentPhoto} />}
        {current.key === "vendor" && <VendorForm value={data.vendor} onChange={update} logoFile={vendorLogo} onLogoFile={setVendorLogo} />}
        {current.key === "event_creator" && <EventCreatorForm value={data.event_creator} onChange={update} logoFile={eventCreatorLogo} onLogoFile={setEventCreatorLogo} />}
        {current.key === "marketplace_user" && <MarketplaceForm value={data.marketplace_user} onChange={update} logoFile={marketplaceLogo} onLogoFile={setMarketplaceLogo} slotFiles={marketplaceSlotFiles} onSlotFile={setMarketplaceSlotFile} />}
        {step === total - 1 && !credsReady && (
          <div className="mt-6 rounded-2xl bg-terracotta-light/40 border-2 border-terracotta/40 p-4" data-testid="bundle-creds-inline">
            <div className="flex items-center gap-2 mb-2">
              <Lock className="w-4 h-4 text-terracotta-dark" />
              <p className="text-xs font-black uppercase tracking-wide text-terracotta-dark">Set your sign-in details</p>
            </div>
            <p className="text-xs text-forest/70 mb-3">We lost the credentials you entered at sign-up. Set them again so you can log in later.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-forest/80">Email</Label>
                <Input type="email" value={credsEmail} onChange={(e) => setCredsEmail(e.target.value)}
                  placeholder="you@example.com" className="mt-1 rounded-xl bg-white border-sage/30" data-testid="bundle-creds-email" />
              </div>
              <div>
                <Label className="text-forest/80">Password (6+ chars)</Label>
                <Input type="password" value={credsPassword} onChange={(e) => setCredsPassword(e.target.value)}
                  placeholder="Choose a password" className="mt-1 rounded-xl bg-white border-sage/30" data-testid="bundle-creds-password" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 mt-6">
        <Button variant="outline" onClick={back} disabled={step === 0 || saving}
          className="rounded-full border-sage/40 text-forest" data-testid="bundle-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
        </Button>
        <Button onClick={next} disabled={saving}
          className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-md px-6" data-testid="bundle-next-btn">
          {step === total - 1 ? "Finish & go to dashboard" : "Save & continue"}
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
      <PendingApprovalDialog open={pendingNotice} onOk={() => navigate("/dashboard")} />
    </div>
  );
}

function PendingApprovalDialog({ open, onOk }) {
  return (
    <Dialog open={open}>
      <DialogContent className="rounded-3xl [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()} data-testid="bundle-pending-dialog">
        <DialogHeader>
          <DialogTitle className="text-forest">All four profiles saved</DialogTitle>
          <DialogDescription className="text-forest/70" data-testid="bundle-pending-text">
            Your Parent profile is live now. Your Vendor, Event Creator and Marketplace profiles are
            pending admin approval and will only go live once approved.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={onOk} className="rounded-full bg-sage hover:bg-sage-dark text-white" data-testid="bundle-pending-ok-btn">
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
